"use client";
import React from 'react';
import { motion } from 'framer-motion';
import ProductCard from './ProductCard';

const ProductGrid2 = ({ products, totalItemsCount }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9, rotateX: -15 },
    visible: { opacity: 1, y: 0, scale: 1, rotateX: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const fallingIcons = ['🧁','🍰','🍪','🥧','🍩','🎂','🍓','🍫','🥜','🍯','🧁','🍰','🍪','🥧','🍩','🎂','🍓','🍫','🥜','🍯'];

  return (
    <motion.div
      className="relative p-3 sm:p-2 lg:p-2 xl:p-2 2xl:p-2 bg-white/80 backdrop-blur-sm shadow-2xl rounded-3xl border-2 border-orange-200 w-full overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      {/* Item count */}
      <motion.div
        className="absolute top-6 right-6 z-20"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <motion.p
          className="text-lg leading-6 text-cyan-900 bg-red-100 px-4 py-2 rounded-full font-semibold shadow-lg border-2 border-red-200"
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
        >
          {totalItemsCount} delicious items
        </motion.p>
      </motion.div>
      <br /><br />

      {/* Falling bakery icons */}
      <div className="absolute inset-0 pointer-events-none">
        {fallingIcons.map((icon, index) => (
          <motion.div
            key={`falling-${index}`}
            className="absolute text-2xl opacity-20"
            style={{ left: `${(index * 5) % 100}%`, top: '-50px' }}
            animate={{ y: [0, window.innerHeight + 100], rotate: [0, 360], opacity: [0, 0.3, 0.2, 0] }}
            transition={{ duration: 8 + Math.random() * 4, repeat: Infinity, delay: index * 0.5, ease: "linear" }}
          >
            {icon}
          </motion.div>
        ))}
      </div>

      {/* Background decorative shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-red-100 to-orange-100 rounded-full opacity-20"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute bottom-10 left-10 w-24 h-24 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full opacity-20"
          animate={{ scale: [1, 1.3, 1], rotate: [360, 180, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Grid content */}
      {totalItemsCount > 0 ? (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 w-full relative z-10 gap-5"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              className="w-full flex justify-center"
              whileInView={{ opacity: 1, y: 0, transition: { duration: 0.6, delay: index * 0.1 } }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <div className="w-full max-w-xs sm:max-w-sm lg:max-w-md">
                <ProductCard product={product} />
              </div>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <motion.div
          className="flex flex-col items-center justify-center py-4 text-center relative z-10"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="text-9xl mb-4"
            animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            🧁
          </motion.div>

          <motion.h3
            className="text-4xl font-bold text-cyan-900 mb-4"
            style={{ fontFamily: "'Sansita Swashed', cursive" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            No cakes found
          </motion.h3>

          <motion.p
            className="text-slate-500 text-lg max-w-md mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            Try adjusting your filters to see more delicious options!
          </motion.p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ProductGrid2;
